import { useCallback, useState } from 'react';
import {
  HUSTLE_REPORT_REASONS,
  formatPence,
  type HustleDecision,
  type HustleReportReason,
  type HustleReportView,
} from '@london-life/contracts';
import { Badge } from '@london-life/ui/components/badge';
import { Button } from '@london-life/ui/components/button';
import { Label } from '@london-life/ui/components/label';
import { Textarea } from '@london-life/ui/components/textarea';
import { FormDialog } from '@london-life/ui/admin/form-dialog';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { useAdmin } from '../lib/admin-context';
import { when } from '../lib/format';
import { useRequest } from '../lib/use-request';

const reasonOf = (reason: string) => HUSTLE_REPORT_REASONS[reason as HustleReportReason] ?? reason;

// Hustle posts players reported (enough reports take a post off the Board until the team looks). Moderators read
// the post and the reports, then remove it for good or put it back. Every decision needs a recent two-step check
// and lands in the audit log.
export function HustleReportsPage() {
  const { can } = useAdmin();
  const [all, setAll] = useState(false);
  const reports = useRequest(useCallback(() => api.admin.hustleReports(all), [all]));
  const [deciding, setDeciding] = useState<{ report: HustleReportView; outcome: HustleDecision['outcome'] } | null>(
    null,
  );
  const [note, setNote] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const decide = async () => {
    if (!deciding) return;
    await api.admin.decideHustleReport(deciding.report.job.id, {
      outcome: deciding.outcome,
      ...(note.trim() ? { note: note.trim() } : {}),
    });
    setDone(deciding.outcome === 'remove' ? 'Post removed.' : 'Post kept.');
    await reports.reload();
  };
  const list = reports.data?.reports ?? [];
  return (
    <section>
      <PageHeader
        title="Hustle reports"
        description="Hustle posts players reported. Read the post and the reports, then remove it or keep it."
      />
      <div className="admin-toolbar">
        <Button type="button" variant={all ? 'outline' : 'default'} onClick={() => setAll(false)}>
          Open{reports.data ? ` (${reports.data.open})` : ''}
        </Button>
        <Button type="button" variant={all ? 'default' : 'outline'} onClick={() => setAll(true)}>
          All reports
        </Button>
      </div>
      {reports.error && <ErrorNotice error={reports.error} onRetry={() => void reports.reload()} />}
      {done && (
        <p role="status" className="admin-muted">
          {done}
        </p>
      )}
      {reports.busy && !reports.data && <p className="admin-muted">Loading…</p>}
      {reports.data && list.length === 0 && (
        <div className="admin-empty">
          <strong className="admin-empty-title">{all ? 'No reports yet.' : 'Nothing to review.'}</strong>
        </div>
      )}
      <ul className="admin-reports">
        {list.map((report) => (
          <li key={report.job.id} className="admin-panel" data-testid="hustle-report">
            <div className="admin-player-head">
              <h2>
                {report.job.label} <span className="admin-muted">by {report.job.poster.name}</span>
              </h2>
              {report.decided ? (
                <Badge variant="secondary">{report.decided === 'removed' ? 'Removed' : 'Kept'}</Badge>
              ) : (
                <Badge>Open</Badge>
              )}
            </div>
            <dl className="admin-facts">
              <dt>The post</dt>
              <dd>
                <strong>{report.job.details}</strong>
              </dd>
              <dt>Fee</dt>
              <dd>{formatPence(report.job.feePence)}</dd>
              <dt>Status</dt>
              <dd>{report.job.status === 'removed' ? 'Off the Board' : report.job.status}</dd>
              <dt>Posted</dt>
              <dd>{when(report.job.createdAt)}</dd>
            </dl>
            <ol className="admin-chat-context" aria-label="The reports">
              {report.reports.map((line, index) => (
                <li key={`${line.at}-${index}`}>
                  <strong>{line.by}</strong> <span>{reasonOf(line.reason)}</span>{' '}
                  <small className="admin-muted">{when(line.at)}</small>
                </li>
              ))}
            </ol>
            {!report.decided && can('players.support') && (
              <div className="admin-page-actions wrap">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    setNote('');
                    setDone(null);
                    setDeciding({ report, outcome: 'remove' });
                  }}
                >
                  Remove post
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setNote('');
                    setDone(null);
                    setDeciding({ report, outcome: 'keep' });
                  }}
                >
                  Keep it
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      <FormDialog
        open={!!deciding}
        title={deciding?.outcome === 'remove' ? 'Remove this post' : 'Keep this post'}
        description={
          deciding?.outcome === 'remove'
            ? 'The post comes down for good and the poster is told. An offer waiting gives its money back.'
            : 'The post goes back on the Board (if its time is not up). The reports are closed.'
        }
        submitLabel={deciding?.outcome === 'remove' ? 'Remove' : 'Keep'}
        onSubmit={decide}
        onClose={() => setDeciding(null)}
      >
        <div className="admin-field">
          <Label htmlFor="hustle-note">Note for the audit log (optional)</Label>
          <Textarea id="hustle-note" maxLength={300} value={note} onChange={(event) => setNote(event.target.value)} />
        </div>
      </FormDialog>
    </section>
  );
}
